import { ElementType, lazy, Suspense } from "react";
import { Navigate, useLocation, useRoutes } from "react-router-dom";
// hooks
import LogoOnlyLayout from "~layouts/LogoOnlyLayout";
import useAuth from "../hooks/useAuth";
// layouts
import AppLayout from "../layouts/app";

import SettingsLayout from "~layouts/settings";

// guards
import AuthGuard from "./AuthGuard";
import GuestGuard from "./GuestGuard";
import AdminGuard from "./AdminGuard";
// config
import { PATH_AFTER_LOGIN } from "../config";
// components
import LoadingScreen from "~components/LoadingScreen";
import BrandLayout from "~src/layouts/brand";
import Search from "~src/pages/search/Search";
import BestSellingProducts from "~src/pages/winningProducts/Products";
import Dashboard from "~src/pages/dashboard";
import CompetitorAnalysisLayout from "~src/layouts/competitor";

// ----------------------------------------------------------------------

// Retry wrapper for lazy-loaded components to handle chunk load failures
const lazyWithRetry = (componentImport: () => Promise<any>) => {
  return lazy(async () => {
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await componentImport();
      } catch (error) {
        lastError = error as Error;
        const isDynamicImportError =
          error instanceof Error &&
          (error.message.includes('Failed to fetch dynamically imported module') ||
           error.message.includes('Failed to fetch') ||
           error.message.includes('Importing a module script failed'));

        if (isDynamicImportError && attempt < maxRetries) {
          // Exponential backoff: 200ms, 400ms, 800ms
          const delay = Math.min(200 * Math.pow(2, attempt - 1), 1000);
          await new Promise(resolve => setTimeout(resolve, delay));
          console.warn(`Retry ${attempt}/${maxRetries} for dynamic import after ${delay}ms`);
        } else {
          throw error;
        }
      }
    }

    throw lastError;
  });
};

const Loadable = (Component: ElementType, options?: { hideProgress?: boolean }) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated } = useAuth();

  const isDashboard = pathname.includes("/dashboard") && isAuthenticated;
  const hideProgress = options?.hideProgress ?? false;

  return (
    <Suspense fallback={<LoadingScreen isDashboard={isDashboard} hideProgress={hideProgress} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: "",
      element: (
        <AuthGuard>
          <AppLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        {
          path: "settings",
          element: <SettingsLayout />,
          children: [
            { path: "profile", element: <Profile /> },
            { path: "general", element: <General /> },
            { path: "plans", element: <Plans /> },
            { path: "billing", element: <Billing /> },
            { path: "security", element: <Security /> },
          ],
        },
        {
          path: "ads",
          children: [
            { element: <TrendingAds />, path: "trending", index: true },
          ],
        },
        {
          path: "checkout/success",
          element: <CheckoutSuccess />,
          index: true,
        },
        {
          path: "brands",
          children: [
            { path: "trending", element: <TrendingBrands />, index: true },
            { path: "discover", element: <DiscoverBrands /> },
            { path: "add-brand", element: <AdminGuard><AddBrand /></AdminGuard> },
            {
              path: ":id",
              element: <BrandLayout />,
              children: [
                {
                  path: "overview",
                  element: <BrandOverview />,
                  index: true,
                },
                {
                  path: "trend-viability",
                  element: <BrandTrendViability />,
                },
                {
                  path: "review-tracker",
                  element: <BrandReviewTracker />,
                },
                {
                  path: "social",
                  element: <BrandSocial />,
                },
                {
                  path: "google-ads",
                  element: <BrandGoogleAds />,
                },
                {
                  path: "fb-ads",
                  element: <BrandFbAds />,
                },
                {
                  path: "traffic-and-revenue",
                  element: <BrandTrafficAndRevenue />,
                },
                {
                  path: "products",
                  element: <BrandProducts />,
                },
              ],
            },
          ],
        },
        {
          path: "products",
          children: [
            { element: <Products />, index: true },
            {
              path: ":brandId/:productVariantId/:productId/:shopifyId/detail/",
              element: <ProductDetail />,
            },
          ],
        },
        {
          path: "best-selling-products",
          children: [{ 
            element: <BestSellingProducts />, 
            index: true 
          }],
        },
        {
          path: "top30-products",
          children: [{ 
            element: <AdminGuard><Top30Products /></AdminGuard>, 
            index: true 
          }],
        },
        {
          path: "competitor-analytics",
          children: [{ element: <CompetitorAnalytics />, index: true }],
        },
        {
          path: "dashboard",
          children: [{ element: <Dashboard />, index: true }],
        },
        {
          path: "competitor-analysis",
          children: [
            {
              element: <CompetitorAnalysisLayout />,
              children: [
                {
                  path: "map-of-brands",
                  element: <CompetitorAnalyticsMapOfBrands />,
                  index: true,
                },
                {
                  path: "map-of-brands-v2",
                  element: <CompetitorAnalyticsMapOfBrandsV2 />,
                  index: true,
                },
                {
                  path: "map-of-brands-v3",
                  element: <CompetitorAnalyticsMapOfBrandsV3 />,
                  index: true,
                },
                {
                  path: "comparison",
                  element: <CompetitorAnalyticsComparison />,
                  index: true,
                },
                {
                  path: "my-tracked-stores",
                  element: <CompetitorAnalyticsMyTrackedStore />,
                  index: true,
                },
              ],
            },
          ],
        },
        {
          path: "search/:searchTerm",
          children: [{ element: <Search />, index: true }],
        },
        {
          path: "creative-center",
          children: [{ element: <Boards />, path: "boards", index: true }],
        },
      ],
    },
    {
      path: "*",
      element: <LogoOnlyLayout />,
      children: [
        { path: "500", element: <Page500 /> },
        { path: "404", element: <Page404 /> },
        { path: "403", element: <Page403 /> },
        { path: "*", element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: "/login",
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      ),
    },
    {
      path: "/register",
      element: <Navigate to="/login" replace />,
    },
    {
      path: "/signup",
      element: <Navigate to="/login" replace />,
    },
    {
      path: "/verify-email",
      element: <Navigate to="/login" replace />,
    },
    {
      path: "/reset-password/verify/:userId/:sessionToken/",
      element: <Navigate to="/login" replace />,
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazyWithRetry(() => import("../pages/auth/Login")));

// CHECKOUT
const CheckoutSuccess = Loadable(
  lazyWithRetry(() => import("../pages/checkout/CheckoutSuccess")),
);

// BRANDS
const TrendingBrands = Loadable(
  lazyWithRetry(() => import("../pages/brands/TrendingBrands")),
);
const DiscoverBrands = Loadable(
  lazyWithRetry(() => import("../pages/brands/DiscoverBrands")),
);

const AddBrand = Loadable(lazyWithRetry(() => import("../pages/brands/AddBrand")));

const BrandOverview = Loadable(
  lazyWithRetry(() => import("../pages/brands/Detail/Overview")),
  { hideProgress: true }
);
const BrandTrendViability = Loadable(
  lazyWithRetry(() => import("../pages/brands/Detail/TrendViability")),
  { hideProgress: true }
);
const BrandReviewTracker = Loadable(
  lazyWithRetry(() => import("../pages/brands/Detail/ReviewTracker")),
  { hideProgress: true }
);
const BrandSocial = Loadable(
  lazyWithRetry(() => import("../pages/brands/Detail/Social")),
  { hideProgress: true }
);
const BrandGoogleAds = Loadable(
  lazyWithRetry(() => import("../pages/brands/Detail/GoogleAds")),
  { hideProgress: true }
);
const BrandFbAds = Loadable(
  lazyWithRetry(() => import("../pages/brands/Detail/FbAds")),
  { hideProgress: true }
);
const BrandTrafficAndRevenue = Loadable(
  lazyWithRetry(() => import("../pages/brands/Detail/TrafficAndRevenue")),
  { hideProgress: true }
);
const BrandProducts = Loadable(
  lazyWithRetry(() => import("../pages/brands/Detail/Products")),
  { hideProgress: true }
);

// ADS
const TrendingAds = Loadable(lazyWithRetry(() => import("../pages/ads/TrendingAds")));

// PRODUCTS
const Products = Loadable(lazyWithRetry(() => import("../pages/products/Products")));
const ProductDetail = Loadable(
  lazyWithRetry(() => import("../pages/products/ProductDetail")),
);
const Top30Products = Loadable(
  lazyWithRetry(() => import("../pages/products/Top30Products")),
);

//COMPETITOR ANALYTICS
const CompetitorAnalytics = Loadable(
  lazyWithRetry(() => import("../pages/competitorAnalytics")),
);

//COMPETITOR ANALYSIS

const CompetitorAnalyticsComparison = Loadable(
  lazyWithRetry(() => import("../pages/competitorAnalysis/Comparison")),
);
const CompetitorAnalyticsMapOfBrands = Loadable(
  lazyWithRetry(() => import("../pages/competitorAnalysis/BrandMapping")),
);
const CompetitorAnalyticsMapOfBrandsV2 = Loadable(
  lazyWithRetry(() => import("../pages/competitorAnalysis/BrandMappingV2")),
);
const CompetitorAnalyticsMapOfBrandsV3 = Loadable(
  lazyWithRetry(() => import("../pages/competitorAnalysis/BrandMappingV3")),
);
const CompetitorAnalyticsMyTrackedStore = Loadable(
  lazyWithRetry(() => import("../pages/competitorAnalysis/MyTrackedStores")),
);

// CREATIVE CENTER
const Boards = Loadable(lazyWithRetry(() => import("../pages/creativeCenter/Boards")));

// SETTINGS
const Profile = Loadable(lazyWithRetry(() => import("../pages/settings/Profile")));
const General = Loadable(lazyWithRetry(() => import("../pages/settings/General")));
const Billing = Loadable(lazyWithRetry(() => import("../pages/settings/Billing")));
const Plans = Loadable(lazyWithRetry(() => import("../pages/settings/Plans")));
const Security = Loadable(lazyWithRetry(() => import("../pages/settings/Security")));

const Page500 = Loadable(lazyWithRetry(() => import("../pages/Page500")));
const Page403 = Loadable(lazyWithRetry(() => import("../pages/Page403")));
const Page404 = Loadable(lazyWithRetry(() => import("../pages/Page404")));
